import React, { useState, useEffect, useMemo } from 'react';

const brandsList = [
  { id: "baseel", name: "Baseel", ar: "بسيل", logo: "/images/logos/baseel.JPG" },
  { id: "rghawy", name: "Rghawy", ar: "رغاوي", logo: "/images/logos/rghawy.png" },
  { id: "xclor", name: "XClor", ar: "إكس كلور", logo: "/images/logos/xclor.JPG" },
  { id: "mival", name: "Mival", ar: "ميفال", logo: "/images/logos/mival.JPG" },
  { id: "tany", name: "Tany", ar: "تاني", logo: "/images/logos/tany.JPG" },
  { id: "cplus", name: "C-Plus", ar: "سي بلاس", logo: "/images/logos/cplus.JPG" }
];

const Brands = ({ lang }) => {
  const isRtl = lang === 'ar';
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEgypt, setIsEgypt] = useState(true);
  
  // --- إضافة حالة الترقيم (Pagination) ---
  const [visibleCount, setVisibleCount] = useState(12); // نبدأ بـ 12 منتجاً كما في المتجر

  const API_URL = 'https://www.yam-ii.com/yam_api/get_products.php';

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_URL);
        const result = await response.json();

        if (result.status === "success") {
          setIsEgypt(result.isEgypt);
          if (Array.isArray(result.products)) {
            setAllProducts(result.products);
          }
        }
      } catch (error) {
        console.error("Fetch Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // تصفية المنتجات حسب الماركة المختارة
  const filteredByBrand = useMemo(() => {
    if (!selectedBrand) return [];
    return allProducts.filter(p => p.brand_en === selectedBrand.name);
  }, [selectedBrand, allProducts]);

  // المنتجات التي ستظهر فعلياً بناءً على الـ Pagination
  const displayedProducts = useMemo(() => {
    return filteredByBrand.slice(0, visibleCount);
  }, [filteredByBrand, visibleCount]);

  const handleBrandClick = (brand) => {
    if (selectedBrand?.id === brand.id) {
      setSelectedBrand(null);
    } else {
      setSelectedBrand(brand);
      setVisibleCount(12); // إعادة الترقيم عند تغيير الماركة
    }
  };

  const loadMore = () => {
    setVisibleCount(prev => prev + 12);
  };

  if (!isEgypt) return null;

  return (
    <div id="brands" className="brands-outer-wrapper" dir={isRtl ? 'rtl' : 'ltr'}>
      <section className="brands-animated-container">
        <div className="brands-badge">{isRtl ? "علاماتنا التجارية" : "OUR BRANDS"}</div>
        
        <div className="brands-inner-content">
          {brandsList.map((brand) => (
            <div 
              key={brand.id} 
              className={`brand-item ${selectedBrand?.id === brand.id ? 'active' : ''}`}
              onClick={() => handleBrandClick(brand)}
            >
              <div className="logo-frame-clear">
                <img src={brand.logo} alt={brand.name} className="brand-logo-img" loading="lazy" />
              </div>
              <div className="brand-names-centered">
                <p className="en-text">{brand.name}</p>
                <p className="ar-text">{brand.ar}</p>
              </div>
            </div>
          ))}
        </div>

        {selectedBrand && (
          <div className="brand-products-reveal">
            <div className="reveal-header">
              <div className="header-title-box">
                <span className="dot"></span>
                <h3>{isRtl ? `منتجات ${selectedBrand.ar}` : `${selectedBrand.name} Products`}</h3>
              </div>
              <button 
                className="close-products-btn" 
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBrand(null);
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="products-grid-large">
              {loading ? (
                <p className="status-text">{isRtl ? "جاري التحميل..." : "Loading..."}</p>
              ) : displayedProducts.length > 0 ? (
                displayedProducts.map((prod) => (
                  <div key={prod.id} className="large-product-card">
                    <div className="large-img-box">
                      <img 
                        // الميزة هنا: الرابط يأتي من الـ API وهو يشير تلقائياً لمجلد optimized بصيغة webp
                        src={prod.image_url} 
                        alt={prod.name_ar} 
                        loading="lazy" 
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/200?text=No+Image'; }}
                      />
                    </div>
                    <div className="large-product-info" style={{ textAlign: isRtl ? 'right' : 'left' }}>
                        <h4>{isRtl ? prod.name_ar : prod.name_en}</h4>
                        <div className="brand-tag">{selectedBrand.name}</div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="status-text">{isRtl ? "قريباً..." : "Coming Soon..."}</p>
              )}
            </div>

            {/* زر عرض المزيد - يظهر فقط إذا كان هناك منتجات أكثر من المعروضة */}
            {!loading && filteredByBrand.length > visibleCount && (
              <div className="load-more-container">
                <button onClick={loadMore} className="load-more-btn">
                  {isRtl ? "عرض المزيد" : "Show More"}
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      <style>{`
        .brands-outer-wrapper { width: 100%; max-width: 100vw; overflow-x: hidden; padding: 60px 15px; background: #ffffff; display: flex; justify-content: center; box-sizing: border-box; }
        .brands-animated-container { position: relative; width: 100%; max-width: 1150px; background: #ffffff; padding: 50px 15px; border-radius: 30px; border: 1px solid rgba(212, 175, 55, 0.2); box-shadow: 0 15px 40px rgba(0,0,0,0.05); z-index: 1; box-sizing: border-box; }
        .brands-animated-container::before { content: ""; position: absolute; top: -1px; left: -1px; right: -1px; bottom: -1px; background: linear-gradient(90deg, transparent, #d4af37, #1a3a5a, transparent); background-size: 200% 200%; border-radius: 31px; z-index: -1; animation: thread-spin 3s linear infinite; mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0); -webkit-mask-composite: destination-out; mask-composite: exclude; padding: 2px; pointer-events: none; }
        @keyframes thread-spin { 0% { background-position: 0% 50%; } 100% { background-position: 200% 50%; } }
        .brands-badge { position: absolute; top: -15px; left: 50%; transform: translateX(-50%); background: #1a3a5a; color: #d4af37; padding: 6px 25px; font-weight: 800; border-radius: 50px; z-index: 10; border: 2px solid #d4af37; white-space: nowrap; font-size: 0.85rem; }
        .brands-inner-content { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 15px; position: relative; z-index: 5; }
        .brand-item { cursor: pointer; text-align: center; width: 100%; }
        .logo-frame-clear { width: 100px; height: 100px; background: #fff; border: 1px solid #f0f0f0; border-radius: 18px; display: flex; align-items: center; justify-content: center; padding: 8px; margin: 0 auto; transition: 0.3s; }
        .brand-logo-img { max-width: 100%; max-height: 100%; object-fit: contain; }
        .brand-item.active .logo-frame-clear { border-color: #d4af37; transform: translateY(-5px); box-shadow: 0 5px 15px rgba(212,175,55,0.1); }
        .brand-names-centered { margin-top: 8px; }
        .en-text { font-weight: 800; color: #1a3a5a; font-size: 0.8rem; margin:0; }
        .ar-text { font-weight: 700; color: #d4af37; font-size: 0.75rem; margin:0; }
        .brand-products-reveal { margin-top: 40px; padding-top: 30px; border-top: 1px solid #f0f0f0; width: 100%; }
        .reveal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px; }
        .header-title-box { display: flex; align-items: center; gap: 8px; }
        .dot { width: 8px; height: 8px; background: #d4af37; border-radius: 50%; }
        .reveal-header h3 { color: #1a3a5a; margin: 0; font-size: 1.1rem; font-weight: bold; }
        .close-products-btn { background: #1a3a5a; color: #fff; border: none; width: 35px; height: 35px; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 50; transition: 0.2s; }
        .close-products-btn:hover { background: #d4af37; }
        .products-grid-large { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 20px; }
        .large-product-card { background: #fff; border-radius: 20px; border: 1px solid #eee; overflow: hidden; transition: 0.3s; }
        .large-img-box { height: 180px; padding: 15px; display: flex; justify-content: center; align-items: center; }
        .large-img-box img { max-width: 100%; max-height: 100%; object-fit: contain; }
        .large-product-info { padding: 15px; background: #fafafa; border-top: 1px solid #f0f0f0; }
        .large-product-info h4 { margin: 0; color: #1a3a5a; font-size: 1rem; font-weight: bold; }
        .brand-tag { color: #d4af37; font-size: 0.75rem; font-weight: bold; margin-top: 5px; }
        .status-text { text-align: center; color: #999; grid-column: 1/-1; padding: 40px; }
        
        .load-more-container { text-align: center; margin-top: 30px; }
        .load-more-btn { padding: 12px 35px; background: #1a3a5a; color: #d4af37; border: 2px solid #d4af37; border-radius: 50px; cursor: pointer; font-weight: bold; transition: 0.3s; }
        .load-more-btn:hover { background: #d4af37; color: #1a3a5a; }

        @media (max-width: 600px) {
          .brands-inner-content { grid-template-columns: repeat(3, 1fr); gap: 10px; }
          .logo-frame-clear { width: 85px; height: 85px; }
          .products-grid-large { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default Brands;